import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import GenPdf from '../GenPdf';
import Header from '../Header/Header';
import NumDossFacAmendment from '../NumDossFacAmendment';
import DocMenu from "../DocMenu";
import LoadingOverlay from "../LoadingOverlay";
import ContractAmendmentInfos from "./ContractAmendment/ContractAmendmentInfos";
import ContractAmendmentTable from "./ContractAmendment/ContractAmendmentTable";
import InformationDialog from '../InformationDialog';
import YesNoDialog from '../YesNoDialog';
import { fetchContractFacAmendmentByDoss, changeContractFacAmendmentNumber } from "../../redux/actions/facAmendmentActions";
import { fetchContrat } from "../../redux/actions/contratActions";
import { changeTitleName, changePdfFileName } from "../../redux/actions/emailAction";
import { getLastInfoEcheance, getDossier, getContactInfos, getContactAmendmentInfos, getTauxhRea, getDevisRea, genContractFacAmendment, getContractFacAmendment } from "./Utils";
import { getContractSigningDate } from "../Utils";
import "../../Styles/FactureAmendment/ContractAmendment.scss";

const ContractAmendment = () => {
  const dispatch = useDispatch();

  const devisrea = useSelector(state => state.devis.devisrea);
  const dossierValue = useSelector(state => state.devis.dossierValue);
  const dossiers = useSelector(state => state.dossier.items);
  const contact = useSelector(state => state.contact.items);
  const echeances = useSelector(state => state.echeances.items);
  const contractFacAmendments = useSelector(state => state.facAmendment.contractFacAmendments);
  const contractFacAmendmentIndex = useSelector(state => state.facAmendment.contractFacAmendmentIndex);

  const [state, setState] = useState({
    company: "",
    operation: "",
    adressOp: "",
    cpOp: "",
    cityOp: "",
    nameMo: "",
    sexeMo: "homme",
    adressMo: "",
    cityMo: "",
    cpMo: "",
    dates: [],
    lastEch: null,
    tauxh: 0,
    duration: 0,
    nbHours: 0,
    paymentMethod: 0,
    nbAmendments: 0,
    file: null,
    isFileOpen: false,
    disableButton: false,
    openDialog: false,
    openDelDialog: false,
  });

  const loadData = useCallback((lastInfosEcheance) => {
    const { lastDateRea, payment_method } = lastInfosEcheance;
    const currentFolder = getDossier(dossiers, dossierValue);
    const idcontact = currentFolder?.idcontact;
    const currentContact = getContactInfos(contact, idcontact);
    const currentContactAmendmentInfos = getContactAmendmentInfos(contractFacAmendments, dossierValue, contractFacAmendmentIndex);

    if (currentContactAmendmentInfos) {
      currentContactAmendmentInfos.dates = currentContactAmendmentInfos.dates ? JSON.parse(currentContactAmendmentInfos.dates) : [];
    }

    if (currentContact) {
      setState(prev => ({
        ...prev,
        nameMo: `${currentContact.prenom} ${currentContact.nom}`,
        sexeMo: currentContact.sexe,
        adressMo: currentContact.adress,
        cityMo: currentContact.city,
        cpMo: currentContact.cp,
        company: currentFolder.sci_sccv,
        operation: currentFolder.operation,
        adressOp: currentFolder.adresse_op,
        cpOp: currentFolder.cp_op,
        cityOp: currentFolder.ville_op,
        lastEch: lastDateRea,
        paymentMethod: currentContactAmendmentInfos?.payment_method || payment_method,
        duration: currentContactAmendmentInfos?.duration || 0,
        nbHours: currentContactAmendmentInfos?.hours || 0,
        tauxh: getTauxhRea(devisrea, dossierValue) || 0,
        dates: currentContactAmendmentInfos?.dates || [],
        file: null,
      }));
    }
  }, [dossiers, dossierValue, contact, contractFacAmendments, contractFacAmendmentIndex, devisrea]);

  const resetValues = useCallback(() => {
    setState(prev => ({
      ...prev,
      company: "",
      operation: "",
      adressOp: "",
      cpOp: "",
      cityOp: "",
      nameMo: "",
      sexeMo: "homme",
      adressMo: "",
      cityMo: "",
      cpMo: "",
      dates: [],
      lastEch: null,
      tauxh: 0,
      duration: 0,
      nbHours: 0,
      paymentMethod: 0,
    }));
  }, []);

  const changePaymentMethod = useCallback((paymentMethod) => {
    setState(prev => ({ ...prev, paymentMethod }));
    createDates(state.duration, paymentMethod);
  }, [state.duration]);

  const createDates = useCallback((duration, paymentMethod_) => {
    const { lastEch, paymentMethod } = state;

    const monthsOffset = (paymentMethod_ || paymentMethod) === 26 ? 2 : 1;
    const dates = [];

    for (let i = 1; i <= duration; i++) {
      const dateech = new Date(lastEch);
      dateech.setMonth(dateech.getMonth() + i * monthsOffset);
      dates.push({
        dateech: dateech.toLocaleDateString("en-CA"),
        raison: `Échéance avenant N° ${i}`,
      });
    }

    const filtered = dates.slice(0, (duration & 1 ? duration + 1 : duration) / monthsOffset);
    setState(prev => ({ ...prev, dates: filtered }));
  }, [state.lastEch, state.paymentMethod]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    if (name === "duration" && value >= 0 && state.lastEch) {
      createDates(+value);
    }
    setState(prev => ({
      ...prev,
      [name]: (value === "" || value < 0 || !state.lastEch) ? 0 : +value
    }));
  }, [state.lastEch, createDates]);

  const setEcheancesDates = useCallback((eventValue, index) => {
    const newDates = [...state.dates];
    const day = new Date(eventValue).getDate();
    for (let i = index; i < newDates.length; i++) {
      if (i === index) {
        newDates[i].dateech = eventValue;
      } else {
        let newDate = new Date(newDates[i].dateech);
        newDate.setDate(day);
        newDates[i].dateech = newDate.toISOString().slice(0, 10);
      }
    }
    setState(prev => ({ ...prev, dates: newDates }));
  }, [state.dates]);

  const handleValidClick = useCallback(async () => {
    try {
      const { paymentMethod, nbAmendments } = state;
      setState(prev => ({ ...prev, duration: 0, dates: [], hours: 0, disableButton: true }));

      await fetch("/api/facamendment/createContractAmendment", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          ndossier: dossierValue,
          amendment_number: nbAmendments + 1,
          duration: 0,
          payment_method: paymentMethod,
          dates: '[]',
          hours: 0,
        }),
      });

      await dispatch(fetchContractFacAmendmentByDoss(dossierValue));
      await dispatch(changeContractFacAmendmentNumber(nbAmendments + 1));
    } catch (error) {
      console.error("Erreur lors de la création de l'avenant :", error);
    } finally {
      setState(prev => ({ ...prev, disableButton: false }));
    }
  }, [state, dossierValue, dispatch]);

  const handleCheckLastIndex = useCallback(async () => {
    if (contractFacAmendmentIndex != state.nbAmendments) {
      setState(prev => ({ ...prev, openDialog: true }));
    } else {
      setState(prev => ({ ...prev, openDelDialog: true }));
    }
  }, [dossierValue, contractFacAmendmentIndex, state])

  const handleDeleteAmendment = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, openDelDialog: false, disableButton: true }));
      await fetch("/api/facamendment/deleteContractAmendment", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          ndossier: dossierValue,
          amendment_number: contractFacAmendmentIndex,
        }),
      });
    } catch (error) {
      console.error("Erreur lors de la suppression de l'avenant :", error);
    } finally {
      await dispatch(fetchContractFacAmendmentByDoss(dossierValue));
      await dispatch(changeContractFacAmendmentNumber(contractFacAmendmentIndex - 1));
      setState(prev => ({ ...prev, disableButton: false, nbAmendments: state.nbAmendments - 1 }));
    }
  }, [dossierValue, contractFacAmendmentIndex]);

  const handleUpdateClick = useCallback(async () => {
    try {
      const { duration, paymentMethod, nbHours, dates } = state;
      setState(prev => ({ ...prev, disableButton: true }));

      await fetch("/api/facamendment/updateContractAmendment", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          ndossier: dossierValue,
          amendment_number: contractFacAmendmentIndex,
          duration,
          payment_method: paymentMethod,
          dates: JSON.stringify(dates),
          hours: nbHours,
        }),
      });
    } catch (error) {
      console.error("Erreur lors de la mise à jour :", error);
    } finally {
      await dispatch(fetchContractFacAmendmentByDoss(dossierValue));
      setState(prev => ({ ...prev, disableButton: false }));
    }
  }, [state, dossierValue, contractFacAmendmentIndex, dispatch]);

  const handleGenClick = useCallback(async () => {
    setState(prev => ({ ...prev, file: null, disableButton: true }));

    const currentDevisRea = getDevisRea(devisrea, dossierValue);
    const json = {
      ndossier: dossierValue,
      ndossier_: dossierValue.toString().split('').join('-'),
      contrat_date: getContractSigningDate(echeances, dossierValue),
      nprop: currentDevisRea.nprop,
      tauxh: +state.tauxh,
      dates: state.dates,
      rea_ht: +currentDevisRea.totalht,
      nb_h_rea: currentDevisRea.totalh,
      nb_h_rea_add: state.nbHours,
      name_mo: state.nameMo,
      sexe_mo: state.sexeMo,
      adress_mo: state.adressMo,
      cp_mo: state.cpMo,
      city_mo: state.cityMo,
      amendment_index: +contractFacAmendmentIndex,
      company: state.company,
      operation: state.operation,
      adress_op: state.adressOp,
      cp_op: state.cpOp,
      city_op: state.cityOp,
      last_ech: state.lastEch,
    };

    const requestOptions = {
      method: "POST",
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(json)
    };

    const blob = await genContractFacAmendment(requestOptions);
    if (blob) {
      setState(prev => ({ ...prev, file: blob, isFileOpen: true }));
    }
    setState(prev => ({ ...prev, disableButton: false }));
  }, [state, dossierValue, echeances, contractFacAmendmentIndex, devisrea, handleUpdateClick]);

  const handleShowClick = useCallback(async () => {
    setState(prev => ({ ...prev, file: null, disableButton: true }));
    const response = await getContractFacAmendment(dossierValue, contractFacAmendmentIndex, 'pdf');

    if (response.blob.size > 100) {
      setState(prev => ({ ...prev, file: response.blob, isFileOpen: true }));
      dispatch(changePdfFileName(response.fileName));
    }
    setState(prev => ({ ...prev, disableButton: false }));
  }, [dossierValue, contractFacAmendmentIndex, dispatch]);

  const handleGetPdfFile = useCallback(async () => {
    setState(prev => ({ ...prev, file: null }));
    const response = await getContractFacAmendment(dossierValue, contractFacAmendmentIndex, 'pdf');
    if (response.blob.size > 100) {
      setState(prev => ({ ...prev, file: response.blob }));
      dispatch(changePdfFileName(response.fileName));
    }
  }, [dossierValue, contractFacAmendmentIndex, dispatch]);

  const handleGetWordFile = useCallback(async () => {
    setState(prev => ({ ...prev, file: null }));
    const response = await getContractFacAmendment(dossierValue, contractFacAmendmentIndex, 'word');
    if (response.blob.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
      setState(prev => ({ ...prev, file: response.blob }));
      dispatch(changePdfFileName(response.fileName));
    }
  }, [dossierValue, contractFacAmendmentIndex, dispatch]);

  const closeDoc = useCallback(() => {
    setState(prev => ({ ...prev, isFileOpen: false }));
  }, []);

  const handleChangeTitleName = useCallback(() => {
    dispatch(changeTitleName(`Contrat Avenant Facturation`));
  }, [dispatch]);

  const getLastDateEch = useCallback(() => {
    if (!contractFacAmendments.length) return null;

    const contractFacAmendmentsSorted = [...contractFacAmendments].sort((a, b) => b.amendment_number - a.amendment_number);
    const lastAmendment = contractFacAmendmentsSorted[contractFacAmendmentsSorted.length - (contractFacAmendmentIndex - 1)];
    const dates = lastAmendment ? JSON.parse(lastAmendment.dates) : [];

    return dates.length > 0 ? dates[dates.length - 1].dateech : null;
  }, [echeances, dossierValue, contractFacAmendments, contractFacAmendmentIndex]);

  useEffect(() => {
    dispatch(changePdfFileName(""));
  }, []);

  useEffect(() => {
    if (!dossierValue) return

    fetchContrat(dossierValue)
  }, [dossierValue])

  useEffect(() => {
    const fetchData = async () => {
      setState(prev => ({ ...prev, disableButton: true }));
      const lastDateEch = await getLastDateEch();
      const lastInfosEcheance = await getLastInfoEcheance(echeances, dossierValue, lastDateEch);

      if (!lastInfosEcheance) {
        dispatch(changePdfFileName(""));
        if (state.lastEch) {
          resetValues();
          setState(prev => ({ ...prev, nbAmendments: contractFacAmendments.length }));
        }
        return;
      }

      if (state.lastEch !== lastInfosEcheance.lastDateRea || state.contractFacAmendmentIndex !== contractFacAmendmentIndex) {
        dispatch(changePdfFileName(""));
        loadData(lastInfosEcheance);
        setState(prev => ({ ...prev, nbAmendments: contractFacAmendments.length }));
      }
      setState(prev => ({ ...prev, disableButton: false }));
    }

    fetchData();
  }, [dossierValue, contractFacAmendmentIndex, echeances, dispatch, loadData, resetValues, state.lastEch]);

  return (
    <div className="contract-amendment">
      {state.disableButton && <LoadingOverlay />}
      <Header />
      <DocMenu
        file={state.file}
        getSourceFile={handleGetWordFile}
        getPDF={handleGetPdfFile}
        handleChangeTitleName={handleChangeTitleName}
      />
      <NumDossFacAmendment type="contractFacAmendment" />
      <h1 className="title">Contrat Avenant Facturation</h1>
      <div className="data">
        <ContractAmendmentInfos
          company={state.company}
          nameMo={state.nameMo}
          operation={state.operation}
          adressOp={state.adressOp}
          cpOp={state.cpOp}
          cityOp={state.cityOp}
          lastEch={state.lastEch}
          tauxh={state.tauxh}
          paymentMethod={state.paymentMethod}
          changePaymentMethod={changePaymentMethod}
          duration={state.duration}
          nbHours={state.nbHours}
          nbAmendments={state.nbAmendments}
          handleChange={handleChange}
        />
        <ContractAmendmentTable
          dates={state.dates}
          duration={state.duration}
          nbHours={state.nbHours}
          disableButton={state.disableButton}
          disableButtonTauxh={state.tauxh === 0}
          handleValidClick={handleValidClick}
          handleUpdateClick={handleUpdateClick}
          handleGenClick={handleGenClick}
          handleShowClick={handleShowClick}
          handleDeleteAmendment={handleCheckLastIndex}
          setEcheancesDates={setEcheancesDates}
        />
        <GenPdf
          isOpen={state.isFileOpen}
          file={state.file}
          closeDoc={closeDoc}
          scale={state.file ? 1 : 0.5}
        />
        <InformationDialog
          isOpen={state.openDialog}
          title={'INFORMATION'}
          information={`Vous ne pouvez pas supprimer l'avenant ${contractFacAmendmentIndex} avant la suppression du dernier avenant ${state.nbAmendments}.`}
          handleClose={() => setState(prev => ({ ...prev, openDialog: false }))}
        />
        <YesNoDialog
          isOpen={state.openDelDialog}
          title={"SUPPRESSION DE L'AVENANT"}
          question={`Êtes-vous sûr de vouloir supprimer l'avenant ?`}
          handleYesClick={handleDeleteAmendment}
          handleClose={() => setState(prev => ({ ...prev, openDelDialog: false }))}
        />
      </div>
    </div>
  );
};

export default ContractAmendment;